import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

// Row virtualization for antd Table WITHOUT giving up native <tr>/<td>
// rendering. We override the tbody (`components.body.wrapper`). antd/rc-table
// still builds every BodyRow element (cheap), but we only mount the rows in the
// visible window (plus a small overscan) and pad the rest with two spacer rows.
// Native rows mean row colors (CSS on <tr>), checkbox selection and onRow keep
// working, and the header/body stay aligned via antd's own colgroup.
//
// IMPORTANT: with scroll.y + horizontal scroll, rc-table renders a hidden
// "measure row" as the first child of the tbody and uses a ResizeObserver on it
// to sync the real column widths into both the header and body colgroups. That
// row (and the empty placeholder) has NO rowKey. We must keep it mounted at all
// times — if virtualization slices it out when scrolled, the measured widths
// collapse and the body columns stop lining up with the header. So we virtualize
// ONLY the data rows (which carry a rowKey) and always render the rest.
//
// Requirements:
//  - Fixed body height via scroll.y (so antd creates the `.ant-table-body`
//    scroll container we listen to).
//  - Every column has a width (fixed table layout, complete colgroup).
//  - Uniform row heights. DataGrid enforces this by defaulting every column in a
//    virtual grid to `ellipsis` (single-line cells); we measure the real height
//    and adapt to it, but a grid whose rows differ in height cannot be placed
//    exactly by a spacer-based virtualizer.

const OVERSCAN = 6;

const isDataRow = (el) => !!(el && el.props && el.props.rowKey !== undefined);

export function createVirtualBody(estimatedRowHeight) {
    return function VirtualTbody({className, children}) {
        const ref = useRef(null);

        const all = React.Children.toArray(children);
        const dataRows = [];
        const otherRows = []; // measure row, empty placeholder, etc. — never sliced
        all.forEach(el => (isDataRow(el) ? dataRows : otherRows).push(el));
        const total = dataRows.length;
        const totalRef = useRef(total);
        totalRef.current = total;

        // Ölçülen satır yüksekliği STATE'tir, ref DEĞİL. Ref olarak tutulduğunda
        // ölçüm o render'a uygulanmıyor, ilk scroll olayına kadar bekliyordu; o
        // olayda boşluk satırları bir anda total * (tahmin - gerçek) kadar
        // değişip sayfayı ~bir ekran boyu zıplatıyordu (tahmin 39px, gerçek
        // yükseklik grid'lerde ~28px).
        const [rowHeight, setRowHeight] = useState(estimatedRowHeight);
        const rowHeightRef = useRef(estimatedRowHeight);
        rowHeightRef.current = rowHeight;

        const [range, setRange] = useState({start: 0, end: Math.min(total, 40)});

        // Son DİKEY konum. `.ant-table-body` scroll olayı yatay kaydırmada da
        // tetikleniyor; yatay hareket dikey pencereyi hiç değiştirmemeli.
        const lastScrollTopRef = useRef(-1);

        const compute = useCallback(() => {
            const scroller = ref.current?.closest('.ant-table-body');
            if (!scroller) return;

            const rh = rowHeightRef.current || estimatedRowHeight;
            const st = scroller.scrollTop;
            const h = scroller.clientHeight || 0;
            lastScrollTopRef.current = st;

            const start = Math.max(0, Math.floor(st / rh) - OVERSCAN);
            const end = Math.min(totalRef.current, Math.ceil((st + h) / rh) + OVERSCAN);
            setRange(prev => (prev.start === start && prev.end === end ? prev : {start, end}));
        }, [estimatedRowHeight]);

        // Ölçüm yalnızca veri/boyut değiştiğinde yapılır. Her render'da ölçmek,
        // kaydırdıkça o an en üstteki satırın yüksekliğini alıp değeri salındırıyordu.
        const needMeasureRef = useRef(true);

        const measure = useCallback(() => {
            if (!needMeasureRef.current) return;

            const firstRow = ref.current?.querySelector('tr[data-row-key]');
            if (!firstRow) return;

            const h = firstRow.getBoundingClientRect().height;
            if (!h) return;

            needMeasureRef.current = false;
            if (Math.abs(h - rowHeightRef.current) <= 1) return;

            // Yükseklik düzeltilirken boşluk satırlarının toplamı da oranla
            // değişiyor. Aynı satır ekranda kalsın diye scrollTop'u aynı oranda
            // taşıyoruz; paint'ten ÖNCE yapıldığı için kullanıcı zıplama görmez.
            const scroller = ref.current?.closest('.ant-table-body');
            if (scroller && scroller.scrollTop > 0) {
                scroller.scrollTop = Math.round(scroller.scrollTop * (h / rowHeightRef.current));
            }

            rowHeightRef.current = h;
            setRowHeight(h);
            compute();
        }, [compute]);

        useLayoutEffect(measure);

        useEffect(() => {
            const scroller = ref.current?.closest('.ant-table-body');
            if (!scroller) return; // no scroll.y => cannot virtualize, render all

            const onScroll = () => {
                if (scroller.scrollTop === lastScrollTopRef.current) return; // yatay kaydırma
                compute();
            };

            const onResize = () => {
                needMeasureRef.current = true; // zoom/font/kolon genişliği değişmiş olabilir
                measure();
                compute();
            };

            needMeasureRef.current = true; // yeni veri geldi
            measure();
            compute();
            scroller.addEventListener('scroll', onScroll, {passive: true});
            window.addEventListener('resize', onResize);
            return () => {
                scroller.removeEventListener('scroll', onScroll);
                window.removeEventListener('resize', onResize);
            };
        }, [total, compute, measure]);

        // Safe fallback: if there is no scroll container, render everything.
        const scroller = ref.current?.closest('.ant-table-body');
        if (!scroller && ref.current) {
            return <tbody ref={ref} className={className}>{otherRows}{dataRows}</tbody>;
        }

        const start = Math.min(range.start, total);
        const end = Math.min(range.end, total);
        const topPad = start * rowHeight;
        const bottomPad = Math.max(0, (total - end) * rowHeight);

        return (
            <tbody ref={ref} className={className}>
                {otherRows}
                {topPad > 0 && (
                    <tr key="virtual-top-spacer" aria-hidden style={{height: topPad}}>
                        <td colSpan={9999} style={{padding: 0, border: 0}}/>
                    </tr>
                )}
                {dataRows.slice(start, end)}
                {bottomPad > 0 && (
                    <tr key="virtual-bottom-spacer" aria-hidden style={{height: bottomPad}}>
                        <td colSpan={9999} style={{padding: 0, border: 0}}/>
                    </tr>
                )}
            </tbody>
        );
    };
}
